import React, { useMemo, useCallback, useRef, useState } from 'react';
import { X, Plus } from 'lucide-react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { colors } from '../../../../styles/tokens';
import { CampaignData, EMAIL_VARIABLES } from '../types';

import { EmailTemplate } from '../hooks/useCampaignState';
import { Spinner } from '../../../ui';

interface EmailEditorModalProps {
  isOpen: boolean;
  campaign: CampaignData;
  editSubject: string;
  editBody: string;
  selectedTemplateId: string | null;
  templates: EmailTemplate[];
  templatesLoading: boolean;
  onClose: () => void;
  onSubjectChange: (value: string) => void;
  onBodyChange: (content: string, delta: any, source: string) => void;
  onTemplateSelect: (template: EmailTemplate | null) => void;
  onSave: () => void;
  onSaveAsTemplate: (name: string, subject: string, body: string) => void;
}

export const EmailEditorModal: React.FC<EmailEditorModalProps> = ({
  isOpen,
  campaign,
  editSubject,
  editBody,
  selectedTemplateId,
  templates,
  templatesLoading,
  onClose,
  onSubjectChange,
  onBodyChange,
  onTemplateSelect,
  onSave,
  onSaveAsTemplate,
}) => {
  const quillRef = useRef<ReactQuill>(null);
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [slashMenuPosition, setSlashMenuPosition] = useState({ top: 0, left: 0 });
  const [slashFilter, setSlashFilter] = useState('');
  const [slashStartIndex, setSlashStartIndex] = useState<number | null>(null);

  const quillModules = useMemo(() => ({
    toolbar: {
      container: [
        ['bold', 'italic', 'underline'],
        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
        ['link'],
        ['clean']
      ]
    }
  }), []);

  const quillFormats = useMemo(() => [
    'bold', 'italic', 'underline',
    'list', 'bullet',
    'link'
  ], []);

  const filteredVariables = EMAIL_VARIABLES.filter(v =>
    v.key.toLowerCase().includes(slashFilter.toLowerCase()) ||
    v.label.toLowerCase().includes(slashFilter.toLowerCase())
  );

  const handleBodyChange = useCallback((content: string, delta: any, source: string) => {
    onBodyChange(content, delta, source);

    if (source !== 'user') return;

    const quill = quillRef.current?.getEditor();
    if (!quill) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const text = quill.getText(0, selection.index);
    const lastSlashIndex = text.lastIndexOf('/');

    if (lastSlashIndex !== -1) {
      const textAfterSlash = text.substring(lastSlashIndex + 1);
      if (!textAfterSlash.includes(' ') && !textAfterSlash.includes('\n')) {
        setSlashFilter(textAfterSlash);
        setSlashStartIndex(lastSlashIndex);

        const bounds = quill.getBounds(selection.index);
        const editorRect = quill.container.getBoundingClientRect();
        setSlashMenuPosition({
          top: bounds.top + editorRect.top + bounds.height + 4,
          left: bounds.left + editorRect.left
        });
        setShowSlashMenu(true);
        return;
      }
    }

    setShowSlashMenu(false);
    setSlashFilter('');
    setSlashStartIndex(null);
  }, [onBodyChange]);

  const insertVariableFromSlash = useCallback((variableKey: string) => {
    const quill = quillRef.current?.getEditor();
    if (!quill || slashStartIndex === null) return;

    const selection = quill.getSelection();
    if (!selection) return;

    const deleteLength = selection.index - slashStartIndex;
    quill.deleteText(slashStartIndex, deleteLength);

    const variable = `{{${variableKey}}}`;
    quill.insertText(slashStartIndex, variable);
    quill.setSelection(slashStartIndex + variable.length, 0);

    setShowSlashMenu(false);
    setSlashFilter('');
    setSlashStartIndex(null);
  }, [slashStartIndex]);

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 z-[200] animate-fadeIn"
        onClick={() => {
          onClose();
          setShowSlashMenu(false);
        }}
      />

      <div className="fixed inset-0 z-[201] flex items-end sm:items-center justify-center sm:p-4">
        <div
          className="bg-white rounded-t-xl sm:rounded-2xl shadow-2xl w-full sm:max-w-3xl flex flex-col overflow-hidden animate-fadeInScale"
          style={{ height: 'min(90vh, 560px)', maxHeight: 'calc(100vh - 20px)' }}
        >
          <div
            className="flex sm:hidden items-center justify-between px-4 py-3 bg-white"
            style={{ borderBottom: `1px solid ${colors.neutral[200]}` }}
          >
            <h3 className="text-[14px] font-semibold" style={{ color: colors.neutral[900] }}>Edit Email</h3>
            <button
              onClick={() => {
                onClose();
                setShowSlashMenu(false);
              }}
              className="p-1.5 rounded-lg transition-colors"
              style={{ color: colors.neutral[400] }}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="flex flex-1 min-h-0">
            <div
              className="hidden sm:flex w-48 flex-shrink-0 flex-col"
              style={{ backgroundColor: colors.neutral[50], borderRight: `1px solid ${colors.neutral[200]}` }}
            >
              <div
                className="h-[57px] flex items-center px-4"
                style={{ borderBottom: `1px solid ${colors.neutral[200]}` }}
              >
                <span className="text-[13px] font-semibold" style={{ color: colors.neutral[900] }}>Templates</span>
              </div>
              <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
                <button
                  onClick={() => {
                    onSubjectChange(campaign.subject);
                    onBodyChange(campaign.body, null, 'api');
                    onTemplateSelect(null);
                  }}
                  className="w-full px-3 py-2.5 rounded-lg text-left transition-all duration-150"
                  style={{
                    backgroundColor: !selectedTemplateId ? colors.white : 'transparent',
                    color: !selectedTemplateId ? colors.neutral[900] : colors.neutral[600],
                    boxShadow: !selectedTemplateId ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                  }}
                >
                  <p className="text-[12px] font-medium truncate">Current Email</p>
                  <p className="text-[10px] truncate mt-0.5" style={{ color: !selectedTemplateId ? colors.neutral[500] : colors.neutral[400] }}>
                    {campaign.subject || 'No subject'}
                  </p>
                </button>

                {templatesLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Spinner size="sm" />
                  </div>
                ) : templates.map(template => (
                  <button
                    key={template.id}
                    onClick={() => {
                      onSubjectChange(template.subject);
                      onBodyChange(template.body, null, 'api');
                      onTemplateSelect(template);
                    }}
                    className="w-full px-3 py-2.5 rounded-lg text-left transition-all duration-150"
                    style={{
                      backgroundColor: selectedTemplateId === template.id ? colors.white : 'transparent',
                      color: selectedTemplateId === template.id ? colors.neutral[900] : colors.neutral[600],
                      boxShadow: selectedTemplateId === template.id ? '0 1px 2px rgba(0,0,0,0.05)' : 'none'
                    }}
                  >
                    <p className="text-[12px] font-medium truncate">{template.name}</p>
                    <p className="text-[10px] truncate mt-0.5" style={{ color: selectedTemplateId === template.id ? colors.neutral[500] : colors.neutral[400] }}>
                      {template.subject}
                    </p>
                  </button>
                ))}
              </nav>

              <div className="h-[65px] flex items-center px-2" style={{ borderTop: `1px solid ${colors.neutral[200]}` }}>
                <button
                  onClick={() => {
                    onSubjectChange('');
                    onBodyChange('', null, 'api');
                    onTemplateSelect({ id: 'new', name: 'New Template', subject: '', body: '' });
                    setNewTemplateName('');
                  }}
                  className="w-full flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-[12px] font-medium transition-colors"
                  style={{
                    backgroundColor: selectedTemplateId === 'new' ? colors.neutral[900] : colors.white,
                    color: selectedTemplateId === 'new' ? colors.white : colors.neutral[700],
                    border: selectedTemplateId === 'new' ? 'none' : `1px solid ${colors.neutral[200]}`
                  }}
                >
                  <Plus className="w-3.5 h-3.5" />
                  New Template
                </button>
              </div>
            </div>

            <div className="flex-1 flex flex-col min-w-0 bg-white">
              <div
                className="hidden sm:flex h-[57px] items-center justify-between px-6 flex-shrink-0"
                style={{ borderBottom: `1px solid ${colors.neutral[200]}` }}
              >
                <h3 className="text-[14px] font-semibold" style={{ color: colors.neutral[900] }}>
                  {selectedTemplateId === 'new' ? 'New Template' : 'Edit Email'}
                </h3>
                <button
                  onClick={() => {
                    onClose();
                    setShowSlashMenu(false);
                  }}
                  className="p-1.5 rounded-lg transition-colors hover:bg-neutral-100"
                  style={{ color: colors.neutral[400] }}
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                {selectedTemplateId === 'new' && (
                  <div className="mb-3">
                    <input
                      type="text"
                      value={newTemplateName}
                      onChange={(e) => setNewTemplateName(e.target.value)}
                      placeholder="Template name..."
                      className="w-full text-[13px] font-medium px-4 py-2.5 rounded-lg bg-neutral-50 border border-neutral-200 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-opacity-10"
                      style={{ color: colors.neutral[900] }}
                      autoFocus
                    />
                  </div>
                )}
                <div className="rounded-xl overflow-hidden" style={{ border: `1px solid ${colors.neutral[200]}` }}>
                  <div className="px-5 py-3" style={{ backgroundColor: colors.neutral[50], borderBottom: `1px solid ${colors.neutral[200]}` }}>
                    <div className="flex items-center gap-3">
                      <div
                        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: colors.neutral[900] }}
                      >
                        <span className="text-white text-[12px] font-medium">
                          {campaign.senderEmail?.charAt(0).toUpperCase() || 'S'}
                        </span>
                      </div>
                      <div className="flex-1 min-w-0">
                        <span className="text-[12px] font-medium" style={{ color: colors.neutral[900] }}>{campaign.senderEmail}</span>
                        <span className="text-[11px] ml-2" style={{ color: colors.neutral[400] }}>→ {'{{first_name}}'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="px-5 py-3" style={{ borderBottom: `1px solid ${colors.neutral[100]}` }}>
                    <input
                      type="text"
                      value={editSubject}
                      onChange={(e) => onSubjectChange(e.target.value)}
                      placeholder="Subject line..."
                      className="w-full text-[14px] font-medium bg-transparent focus:outline-none"
                      style={{ color: colors.neutral[900] }}
                    />
                  </div>

                  <div className="relative">
                    <style>{`
                      .email-preview-editor .ql-toolbar {
                        border: none;
                        border-bottom: 1px solid #f3f4f6;
                        background: transparent;
                        padding: 8px 12px;
                        display: flex;
                        align-items: center;
                        gap: 2px;
                      }
                      .email-preview-editor .ql-toolbar .ql-formats {
                        display: flex;
                        align-items: center;
                        gap: 1px;
                        margin-right: 8px;
                        padding-right: 8px;
                        border-right: 1px solid #e5e7eb;
                      }
                      .email-preview-editor .ql-toolbar .ql-formats:last-child {
                        margin-right: 0;
                        padding-right: 0;
                        border-right: none;
                      }
                      .email-preview-editor .ql-toolbar button {
                        width: 28px;
                        height: 28px;
                        padding: 4px;
                        border-radius: 6px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                      }
                      .email-preview-editor .ql-toolbar button svg {
                        width: 14px;
                        height: 14px;
                      }
                      .email-preview-editor .ql-toolbar button:hover {
                        background: #f3f4f6;
                      }
                      .email-preview-editor .ql-toolbar button.ql-active {
                        background: #e5e7eb;
                        color: #111827;
                      }
                      .email-preview-editor .ql-container {
                        border: none;
                        font-family: inherit;
                        font-size: 13px;
                      }
                      .email-preview-editor .ql-editor {
                        min-height: 200px;
                        max-height: 260px;
                        padding: 14px 18px;
                        line-height: 1.65;
                        color: #374151;
                      }
                      .email-preview-editor .ql-editor p {
                        margin-bottom: 8px;
                      }
                      .email-preview-editor .ql-editor ul, .email-preview-editor .ql-editor ol {
                        padding-left: 18px;
                        margin-bottom: 8px;
                      }
                      .email-preview-editor .ql-editor li {
                        margin-bottom: 3px;
                      }
                      .email-preview-editor .ql-editor.ql-blank::before {
                        font-style: normal;
                        color: #9ca3af;
                        font-size: 13px;
                      }
                    `}</style>
                    <div className="email-preview-editor">
                      <ReactQuill
                        ref={quillRef}
                        theme="snow"
                        value={editBody}
                        onChange={handleBodyChange}
                        modules={quillModules}
                        formats={quillFormats}
                        placeholder="Write your email... Type / to insert variables"
                      />
                    </div>

                    {showSlashMenu && filteredVariables.length > 0 && (
                      <div
                        className="fixed bg-white rounded-xl shadow-2xl border border-neutral-200 py-1.5 z-[300] min-w-[200px]"
                        style={{ top: slashMenuPosition.top, left: slashMenuPosition.left }}
                      >
                        <div className="px-3 py-1.5 text-[10px] font-medium text-neutral-400 uppercase tracking-wide">
                          Variables
                        </div>
                        {filteredVariables.map((variable) => (
                          <button
                            key={variable.key}
                            onClick={() => insertVariableFromSlash(variable.key)}
                            className="w-full px-3 py-2 text-left hover:bg-neutral-50 flex items-center justify-between gap-3 group transition-colors"
                          >
                            <div>
                              <p className="text-[12px] font-medium text-neutral-900">{variable.label}</p>
                              <p className="text-[10px] text-neutral-400">{variable.description}</p>
                            </div>
                            <code className="text-[9px] bg-neutral-100 text-neutral-500 px-1.5 py-0.5 rounded font-mono group-hover:bg-neutral-900 group-hover:text-white transition-colors">
                              /
                            </code>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <p className="mt-3 text-[11px] text-neutral-400 text-center">
                  Type <kbd className="px-1 py-0.5 bg-neutral-100 rounded text-neutral-500 font-mono text-[10px]">/</kbd> to insert personalization variables
                </p>
              </div>

              <div
                className="h-[60px] sm:h-[65px] px-4 sm:px-6 flex items-center justify-end gap-2 flex-shrink-0 bg-white"
                style={{ borderTop: `1px solid ${colors.neutral[200]}` }}
              >
                <button
                  onClick={() => {
                    onClose();
                    setShowSlashMenu(false);
                  }}
                  className="px-3 sm:px-4 py-2 text-[12px] sm:text-[13px] font-medium rounded-lg transition-colors hover:bg-neutral-100"
                  style={{ color: colors.neutral[600] }}
                >
                  Cancel
                </button>
                <button
                  onClick={() => {
                    if (selectedTemplateId === 'new') {
                      const name = newTemplateName.trim() || 'Untitled Template';
                      onSaveAsTemplate(name, editSubject, editBody);
                      setNewTemplateName('');
                    } else {
                      onSave();
                    }
                    onClose();
                    setShowSlashMenu(false);
                  }}
                  className="px-3 sm:px-4 py-2 text-[12px] sm:text-[13px] font-medium text-white rounded-lg transition-colors"
                  style={{ backgroundColor: colors.neutral[900] }}
                >
                  {selectedTemplateId === 'new' ? 'Save Template' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
